import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from "react";
import { ChevronDown } from "lucide-react";
const AccordionContext = React.createContext(null);
function useAccordionContext() {
    const context = React.useContext(AccordionContext);
    if (!context) {
        throw new Error("Accordion compound components must be used within Accordion.");
    }
    return context;
}
function AccordionPanel({ id, open, children, }) {
    return (_jsx("div", { id: `accordion-panel-${id}`, role: "region", "aria-labelledby": `accordion-trigger-${id}`, hidden: !open, className: open ? "pb-4 pt-1 text-sm text-zinc-600" : undefined, children: open ? children : null }));
}
export function AccordionItem({ id, title, disabled = false, className = "", children }) {
    const { openIds, toggle } = useAccordionContext();
    const open = openIds.includes(id);
    return (_jsxs("div", { className: ["px-4 sm:px-5", className].join(" "), children: [_jsxs("button", { id: `accordion-trigger-${id}`, type: "button", disabled: disabled, "aria-expanded": open, "aria-controls": `accordion-panel-${id}`, onClick: () => toggle(id), className: [
                    "flex w-full items-center justify-between gap-3 py-3 text-left text-sm font-medium text-zinc-800",
                    "transition-colors hover:text-zinc-900 disabled:cursor-not-allowed disabled:opacity-50",
                ].join(" "), children: [_jsx("span", { children: title }), _jsx(ChevronDown, { className: [
                            "h-4 w-4 shrink-0 text-zinc-400 transition-transform duration-200",
                            open ? "rotate-180" : "",
                        ].join(" "), "aria-hidden": true })] }), _jsx(AccordionPanel, { id: id, open: open, children: children })] }));
}
export function Accordion({ items, value, defaultValue = [], onValueChange, type = "single", className = "", children, }) {
    const [internalValue, setInternalValue] = React.useState(defaultValue);
    const openIds = value ?? internalValue;
    const setOpenIds = React.useCallback((next) => {
        if (value === undefined) {
            setInternalValue(next);
        }
        onValueChange?.(next);
    }, [onValueChange, value]);
    const toggle = React.useCallback((id) => {
        const isOpen = openIds.includes(id);
        if (type === "multiple") {
            setOpenIds(isOpen ? openIds.filter((itemId) => itemId !== id) : [...openIds, id]);
            return;
        }
        setOpenIds(isOpen ? [] : [id]);
    }, [openIds, setOpenIds, type]);
    const contextValue = React.useMemo(() => ({ openIds, toggle }), [openIds, toggle]);
    return (_jsx(AccordionContext.Provider, { value: contextValue, children: _jsx("div", { className: ["divide-y divide-zinc-200 rounded-xl border border-zinc-200 bg-white shadow-xs ring-1 ring-black/[0.07]", className].join(" "), children: items
                ? items.map((item) => (_jsx(AccordionItem, { id: item.id, title: item.title, disabled: item.disabled, children: item.content }, item.id)))
                : children }) }));
}
